import { Injectable, Inject } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Mist } from '@app/shared/models/mist/mist.model';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { timeout, retry, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class MistService {
    private apiUrl = `${environment.apiHost}/mist`;

    constructor(private http: HttpClient, @Inject(OKTA_AUTH) public oktaAuth: OktaAuth) {
    }

    async findByStoreNumber(storeNumber: string) {
        return this.http.get<Mist>(`${this.apiUrl}/access-points?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(4000),
                retry(3),
                catchError(error => of("Request for mists timed out!"))
            );
    }

    async getAccessPointsDetails(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/access-point-details?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(4000),
                retry(3),
                catchError(error => of("Request for mists timed out!"))
            );
    }

    async getAllMists() {
        return this.http.get(`${this.apiUrl}/all-access-points`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(6000),
                retry(3),
                catchError(error => of("Request for all mists timed out!"))
            );
    }

    async getAllMistsLatent() {
        return this.http.get(`${this.apiUrl}/all-access-points-latent`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(6000),
                retry(3),
                catchError(error => of("Request for all mists timed out!"))
            );
    }

    async getAccessPointStats(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/access-point-stats?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(1500),
                retry(3),
                catchError(error => of("Request for mist access point stats timed out!"))
            );
    }

    async getRogueAccessPointAudit() {
        return this.http.get(`${this.apiUrl}/rogue-access-points-audit`,
            {
                headers: new HttpHeaders(
                    {"Authorization": "Bearer " + await this.oktaAuth.getAccessToken()}
                )
            }).pipe(
                timeout(1500),
                retry(3),
                catchError(error => of("Request for mist rogue access point audit timed out!"))
            )
    }

    async getSite(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/site?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    {"Authorization": "Bearer " + await this.oktaAuth.getAccessToken(),
                    "Cache-Control": "max-age=0"}
                )
            }).pipe(
                timeout(5000),
                retry(3),
                catchError(error => of("Request for mist sites timed out!"))
            )
    }

    async getSitegroups() {
        return this.http.get(`${this.apiUrl}/sitegroups`,
            {
                headers: new HttpHeaders(
                    {"Authorization": "Bearer " + await this.oktaAuth.getAccessToken(),
                    "Cache-Control": "max-age=0"}
                )
            }).pipe(
                timeout(5000),
                retry(3),
                catchError(error => of("Request for mist sitegroups timed out!"))
            )
    }

    async addSitegroup(siteId: string, sitegroupId: string) {
        return this.http.post(`${this.apiUrl}/site/add-sitegroup`,
            {
                "siteId": siteId,
                "sitegroupId": sitegroupId
            },
            {
                headers: new HttpHeaders(
                    {
                        "Authorization": "Bearer " + await this.oktaAuth.getAccessToken()
                    }
                )
            }
        ).pipe(
            timeout(5000),
            retry(3),
            catchError(error => of("Request to add Mist site group timed out!"))
        );
    }

    async removeSiteGroup(siteId: string, sitegroupId: string) {
        return this.http.post(`${this.apiUrl}/site/remove-sitegroup`,
            {
                "siteId": siteId,
                "sitegroupId": sitegroupId
            },
            {
                headers: new HttpHeaders(
                    {
                        "Authorization": "Bearer " + await this.oktaAuth.getAccessToken()
                    }
                )
            }
        ).pipe(
            timeout(5000),
            retry(3),
            catchError(error => of("Request to remove Mist site group timed out!"))
        );
    }
}